import React, { type ReactElement } from "react";
import { CalendarDays } from "lucide-react";
import { Button, Skeleton } from "@ndd/ui";
import type {
  RdScheduleBlock,
  RdScheduleWeek,
} from "../../../../shared/rd-schedule.js";
import {
  buildWeekGrid,
  requestedWeekTitle,
  weekSummary,
  type WeekGridLayout,
} from "../../lib/rd-schedule-week.js";
import { WeekGrid } from "./week-grid.js";
import { WeekSummaryRow, type WeekSummaryState } from "./week-summary.js";
import { WeekKey, WeekToolbar } from "./week-toolbar.js";

// NO-874: the Week view under My Appointments. Presentational: the page holds
// the query and the viewer's choices (missed filter, key) and hands
// them in, so every state here renders without a provider.

export type WeekViewData =
  | { kind: "ready"; week: RdScheduleWeek; refreshing: boolean }
  | { kind: "loading"; weekStart: string }
  | { kind: "error"; weekStart: string; onRetry: () => void };

export interface WeekViewProps {
  data: WeekViewData;
  showMissed: boolean;
  onShowMissedChange: (checked: boolean) => void;
  keyOpen: boolean;
  onKeyOpenChange: (open: boolean) => void;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  /** The seam the details panel (NO-875) plugs into. */
  onOpenDetails: (appointmentId: string) => void;
  /** The appointment whose details panel is open, ringed on the grid. */
  selectedId?: string | null;
  /** The RD's own blocked time was activated (NO-877). */
  onOpenBlock?: (block: RdScheduleBlock) => void;
  /** Opens the Availability view (NO-878). */
  onSetHours: (() => void) | null;
  /** Opens another week (NO-907: the week the saved hours start). */
  onOpenWeek: (week: string) => void;
}

export function WeekView(props: WeekViewProps): ReactElement {
  const { data } = props;
  const ready = data.kind === "ready" ? data.week : null;
  const layout = ready ? buildWeekGrid(ready, { showMissed: props.showMissed }) : null;
  const title =
    layout?.title ??
    requestedWeekTitle(data.kind === "ready" ? data.week.weekStart : data.weekStart);

  return (
    <div>
      <WeekToolbar
        title={title}
        onPrevious={props.onPreviousWeek}
        onNext={props.onNextWeek}
        onToday={props.onToday}
        showMissed={props.showMissed}
        onShowMissedChange={props.onShowMissedChange}
        keyOpen={props.keyOpen}
        onKeyOpenChange={props.onKeyOpenChange}
      />
      {props.keyOpen ? <WeekKey onClose={() => props.onKeyOpenChange(false)} /> : null}
      <WeekSummaryRow
        state={summaryState(data)}
        onSelectClient={props.onOpenDetails}
        onSetHours={props.onSetHours}
        onOpenWeek={props.onOpenWeek}
      />
      <WeekBody data={data} layout={layout} props={props} />
    </div>
  );
}

/** The card over the grid is for a calendar with nothing on it: a RD whose hours are unset but who has visits still needs to click them. */
function showsNoHoursCard(week: RdScheduleWeek): boolean {
  return (
    week.hours.state === "no_hours_set" &&
    week.appointments.length === 0 &&
    week.blocks.length === 0
  );
}

function WeekBody({
  data,
  layout,
  props,
}: {
  data: WeekViewData;
  layout: WeekGridLayout | null;
  props: WeekViewProps;
}): ReactElement {
  if (data.kind === "ready" && layout) {
    return (
      <WeekGrid
        layout={layout}
        selectedId={props.selectedId ?? null}
        onOpenDetails={props.onOpenDetails}
        onOpenBlock={props.onOpenBlock}
        emptyCard={
          showsNoHoursCard(data.week)
            ? {
                onSetHours: props.onSetHours,
                startsOn: data.week.hoursStartsOn,
                onOpenWeek: props.onOpenWeek,
              }
            : null
        }
        busy={data.refreshing}
      />
    );
  }

  if (data.kind === "error") return <WeekError onRetry={data.onRetry} />;

  return <WeekSkeleton />;
}

function summaryState(data: WeekViewData): WeekSummaryState {
  if (data.kind === "ready") return { kind: "ready", summary: weekSummary(data.week) };

  if (data.kind === "error") return { kind: "unavailable" };

  return { kind: "loading" };
}

function WeekSkeleton(): ReactElement {
  return (
    <div
      role="status"
      aria-label="Loading this week"
      className="rounded-xl border border-border bg-card p-4"
    >
      <div className="grid grid-cols-5 gap-3">
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton key={index} className="h-5 w-24" />
        ))}
      </div>
      <Skeleton className="mt-4 h-[460px] w-full rounded-lg" />
    </div>
  );
}

function WeekError({ onRetry }: { onRetry: () => void }): ReactElement {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-2.5 rounded-xl border border-dashed border-border-strong bg-card px-6 py-12 text-center"
    >
      <CalendarDays
        aria-hidden="true"
        className="size-9 text-muted-foreground"
        strokeWidth={1.25}
      />
      <h3 className="font-sans text-title font-semibold leading-title text-foreground">
        {"Couldn’t load this week"}
      </h3>
      <p className="max-w-md text-muted-foreground">
        Your calendar is unavailable right now. Nothing on it has changed.
      </p>
      <Button type="button" variant="utility" size="compact" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
